/**
 * Which crown tiles are worth asking the worker for. A crown object may carry a mask — a few
 * polygons, such as the units a report has picked — and a tile that touches none of them is
 * served empty straight away: no work in the worker, nothing drawn far from the picks. The
 * test is per tile, never per crown, so a mask costs the same however many crowns there are.
 */
import type { Geometry, Position } from 'geojson';

/** [west, south, east, north] in degrees. */
export type Box = [number, number, number, number];

/** The lng/lat box of a web-mercator tile. */
export function tileBounds(z: number, x: number, y: number): Box {
  const n = 2 ** z;
  const lng = (i: number) => (i / n) * 360 - 180;
  const lat = (j: number) => (Math.atan(Math.sinh(Math.PI * (1 - (2 * j) / n))) * 180) / Math.PI;
  return [lng(x), lat(y + 1), lng(x + 1), lat(y)];
}

const overlaps = (a: Box, b: Box) => a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];

function rings(g: Geometry): Position[][][] {
  return g.type === 'Polygon' ? [g.coordinates] : g.type === 'MultiPolygon' ? g.coordinates : [];
}

function boxOf(g: Geometry): Box | null {
  let box: Box | null = null;
  for (const polygon of rings(g))
    for (const [x, y] of polygon[0] ?? []) box = box ? [Math.min(box[0], x!), Math.min(box[1], y!), Math.max(box[2], x!), Math.max(box[3], y!)] : [x!, y!, x!, y!];
  return box;
}

function inRing(x: number, y: number, ring: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi! > y !== yj! > y && x < ((xj! - xi!) * (y - yi!)) / (yj! - yi!) + xi!) inside = !inside;
  }
  return inside;
}

/** Whether a segment crosses the rectangle (Liang–Barsky clipping). */
function segmentHitsBox([x0, y0]: Position, [x1, y1]: Position, [w, s, e, n]: Box): boolean {
  let t0 = 0;
  let t1 = 1;
  const dx = x1! - x0!;
  const dy = y1! - y0!;
  for (const [p, q] of [
    [-dx, x0! - w],
    [dx, e - x0!],
    [-dy, y0! - s],
    [dy, n - y0!],
  ] as const) {
    if (p === 0) {
      if (q < 0) return false;
    } else {
      const r = q / p;
      if (p < 0) t0 = Math.max(t0, r);
      else t1 = Math.min(t1, r);
      if (t0 > t1) return false;
    }
  }
  return true;
}

/** Whether a polygon (holes aside: a box inside a hole still counts) and a rectangle share any point. */
export function boxHitsGeometry(box: Box, g: Geometry): boolean {
  return rings(g).some(([outer]) => {
    if (!outer?.length) return false;
    // An edge crossing the box, a polygon wholly inside it (its edges cross too, or lie inside), or the box inside the polygon.
    for (let i = 1; i < outer.length; i++) if (segmentHitsBox(outer[i - 1]!, outer[i]!, box)) return true;
    return inRing(box[0], box[1], outer);
  });
}

interface Shape {
  box: Box;
  geometry: Geometry;
}

interface Mask {
  shapes: Shape[];
  /** Bumped whenever the mask changes: what was filtered under an older one is stale. */
  version: number;
}

const masks = new Map<string, Mask>();
let versions = 0;

/** Keep only the tiles of `object` that touch one of `geometries`; null lifts the mask. */
export function setCrownTileMask(object: string, geometries: Geometry[] | null): void {
  if (!geometries) {
    masks.delete(object);
    return;
  }
  masks.set(object, {
    version: ++versions,
    shapes: geometries.flatMap((geometry) => {
      const box = boxOf(geometry);
      return box ? [{ box, geometry }] : [];
    }),
  });
}

/** The mask of an object as it stands: its polygons and version; null without one. */
export function crownTileMask(object: string): { geometries: Geometry[]; version: number } | null {
  const mask = masks.get(object);
  return mask ? { geometries: mask.shapes.map((s) => s.geometry), version: mask.version } : null;
}

/** Whether the tile is to be served; true for an object without a mask. */
export function tileWanted(object: string, z: number, x: number, y: number): boolean {
  return tileState(object, z, x, y) !== 'out';
}

/** Whether a rectangle lies wholly inside a polygon: its corners inside, outside every hole, and no ring crossing it. */
export function boxWithinGeometry(box: Box, g: Geometry): boolean {
  const corners: [number, number][] = [
    [box[0], box[1]],
    [box[2], box[1]],
    [box[2], box[3]],
    [box[0], box[3]],
  ];
  return rings(g).some((polygon) => {
    const [outer, ...holes] = polygon;
    if (!outer?.length || !corners.every(([x, y]) => inRing(x, y, outer))) return false;
    for (const ring of polygon) for (let i = 1; i < ring.length; i++) if (segmentHitsBox(ring[i - 1]!, ring[i]!, box)) return false;
    return !holes.some((h) => h.length && inRing(h[0]![0]!, h[0]![1]!, outer) && corners.some(([x, y]) => inRing(x, y, h)));
  });
}

/**
 * Where a tile stands against an object's mask: `out` touches no masked polygon (served empty),
 * `in` lies wholly inside one (served as it is), `edge` crosses a boundary (its features are
 * kept by their anchor); `in` for an object without a mask.
 */
export function tileState(object: string, z: number, x: number, y: number): 'in' | 'edge' | 'out' {
  const mask = masks.get(object);
  if (!mask) return 'in';
  const tile = tileBounds(z, x, y);
  const touching = mask.shapes.filter((s) => overlaps(s.box, tile) && boxHitsGeometry(tile, s.geometry));
  if (!touching.length) return 'out';
  return touching.some((s) => boxWithinGeometry(tile, s.geometry)) ? 'in' : 'edge';
}
